import React from 'react';
import { PRESET_ICONS } from '../../data/presetIcons';

/**
 * What sits beside a product, wherever a product is listed.
 *
 * ONE LADDER, ONE PLACE. The host chooses ONE way to show a product, and the
 * precedence below is that choice read back:
 *
 *   1. a preset glyph  — an explicit "show an icon, not a photo"
 *   2. `heroImageUrl`  — the product's own picture
 *   3. `iconUrl`       — the legacy per-option upload, only on rows migrated
 *                        from `service_options` before products existed
 *   4. a default glyph — so a product with no imagery still has a shape
 *
 * A preset comes first because picking one is a decision, and photos may still
 * be stored from before the host switched. Copying this ladder into each list
 * is how the same pack came to show a picture on the booking page and a bare
 * title in the dialog that sells it.
 *
 * ONE FIXED BOX whatever the case. The box is the only thing holding the text
 * column in place, so a photo row and a glyph row must reserve exactly the same
 * width — otherwise every title in a list starts somewhere slightly different.
 * A photo fills it; a glyph sits centred inside at its own smaller size.
 */

export interface ThumbnailProduct {
  heroImageUrl?: string | null;
  iconUrl?: string | null;
  iconType?: 'preset' | 'uploaded' | null;
  iconPreset?: string | null;
}

interface ProductThumbnailProps {
  product: ThumbnailProduct;
  /** Box size. `sm` for dense lists inside a dialog, `md` for the option list. */
  size?: 'sm' | 'md';
  className?: string;
}

const BOX = {
  sm: { box: 'w-10 h-10', glyph: 'h-5 w-5' },
  md: { box: 'w-16 h-16', glyph: 'h-7 w-7' },
} as const;

const ProductThumbnail: React.FC<ProductThumbnailProps> = ({
  product,
  size = 'md',
  className = '',
}) => {
  const { box, glyph } = BOX[size];
  const preset =
    product.iconType === 'preset' && product.iconPreset
      ? PRESET_ICONS[product.iconPreset]
      : undefined;

  return (
    <div
      className={`${box} rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden ${className}`}
    >
      {preset ? (
        <preset.icon className={glyph} style={{ color: 'var(--brand-color, #2E7AA0)' }} />
      ) : product.heroImageUrl ? (
        // `alt=""` on purpose: the title sits right beside it, so a description
        // here would have a screen reader announce the same product twice.
        <img src={product.heroImageUrl} alt="" className="w-full h-full object-cover" />
      ) : product.iconUrl ? (
        <img src={product.iconUrl} alt="" className="w-full h-full object-cover" />
      ) : (
        <svg
          className={glyph}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          style={{ color: 'var(--brand-color, #2E7AA0)' }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      )}
    </div>
  );
};

export default ProductThumbnail;
